import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AddOnServicesSection = ({ onRegisterClick }: { onRegisterClick: () => void }) => {
  const { t } = useTranslation('choHem');
  const [flipped, setFlipped] = useState<Record<string, boolean>>({});

  const toggleFlip = (id: string) => {
    setFlipped((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const plans = [
    {
      id: 'starter',
      name: t('addon.starter.name'),
      subtitle: t('addon.starter.subtitle'),
      price: t('addon.starter.price'),
      badge: t('addon.starter.badge'),
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
      accentColor: 'text-emerald-400',
      highlight: false,
      features: [
        t('addon.feature.starter1'),
        t('addon.feature.starter2'),
        t('addon.feature.starter3'),
        t('addon.feature.starter4'),
        t('addon.feature.starter5'),
      ],
    },
    {
      id: 'business',
      name: t('addon.business.name'),
      subtitle: t('addon.business.subtitle'),
      price: t('addon.business.price'),
      badge: t('addon.business.badge'),
      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
      accentColor: 'text-amber-400',
      highlight: true,
      features: [
        t('addon.feature.business1'),
        t('addon.feature.business2'),
        t('addon.feature.business3'),
        t('addon.feature.business4'),
      ],
    },
    {
      id: 'premium',
      name: t('addon.premium.name'),
      subtitle: t('addon.premium.subtitle'),
      price: t('addon.premium.price'),
      badge: t('addon.premium.badge'),
      badgeColor: 'bg-violet-500/20 text-violet-300 border-violet-400/30',
      accentColor: 'text-violet-400',
      highlight: false,
      features: [
        t('addon.feature.premium1'),
        t('addon.feature.premium2'),
        t('addon.feature.premium3'),
        t('addon.feature.premium4'),
        t('addon.feature.premium5'),
        t('addon.feature.premium6'),
      ],
    },
  ];

  const extras = [
    t('addon.extra1'),
    t('addon.extra2'),
    t('addon.extra3'),
    t('addon.extra4'),
    t('addon.extra5'),
  ];

  return (
    <section className="py-14 md:py-24 bg-gradient-to-b from-gray-950 to-black" id="addon-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-block px-3 py-1 bg-amber-500/15 border border-amber-400/30 rounded-full text-xs text-amber-300 font-semibold uppercase tracking-wider mb-3">
            {t('addon.badge')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white font-serif mb-3">
            {t('addon.title')} <span className="text-amber-400">{t('addon.titleHighlight')}</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
            {t('addon.subtitle')}
          </p>
        </div>

        {/* ── DESKTOP: giữ nguyên ── */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 mb-10">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-7 flex flex-col transition-all duration-300 hover:scale-[1.02] ${
                plan.highlight
                  ? 'bg-gradient-to-b from-amber-500/15 to-amber-600/5 border-amber-400/40 shadow-xl shadow-amber-500/10'
                  : 'bg-white/5 border-white/10'
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-amber-500 text-black text-xs font-bold rounded-full whitespace-nowrap">
                    {t('addon.popularBadge')}
                  </span>
                </div>
              )}
              <div className="mb-4">
                <span className={`inline-block px-2.5 py-0.5 text-[10px] font-semibold rounded-full border ${plan.badgeColor} mb-3`}>
                  {plan.badge}
                </span>
                <h3 className="text-white font-bold text-xl leading-tight">{plan.name}</h3>
                <p className="text-amber-400 text-sm font-medium mt-0.5">{plan.subtitle}</p>
              </div>
              <div className="mb-5 p-4 bg-black/30 rounded-xl border border-white/10">
                <div className="flex items-end gap-1">
                  <span className="text-4xl font-bold text-amber-400">{plan.price}</span>
                  <span className="text-gray-400 text-sm mb-1">{t('addon.perMonth')}</span>
                </div>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                    <i className="ri-check-line text-amber-400 mt-0.5 flex-shrink-0"></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onRegisterClick}
                className={`w-full py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  plan.highlight
                    ? 'bg-amber-500 text-black hover:bg-amber-400'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                {t('addon.flipCta')}
              </button>
            </div>
          ))}
        </div>

        {/* ── MOBILE: flip card ── */}
        <div className="md:hidden mb-8">
          <p className="text-center text-gray-500 text-xs mb-5 flex items-center justify-center gap-1.5">
            <i className="ri-hand-coin-line text-amber-400"></i>
            {t('addon.flipHint')}
          </p>
          <div className="grid grid-cols-1 gap-5">
            {plans.map((plan) => {
              const isFlipped = !!flipped[plan.id];
              return (
                <div
                  key={plan.id}
                  className="relative"
                  style={{ perspective: '1000px', height: '260px' }}
                >
                  <div
                    className="w-full h-full transition-transform duration-500 cursor-pointer"
                    style={{
                      transformStyle: 'preserve-3d',
                      transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    }}
                    onClick={() => toggleFlip(plan.id)}
                  >
                    {/* Mặt trước */}
                    <div
                      className={`absolute inset-0 rounded-2xl border p-5 flex flex-col justify-between ${
                        plan.highlight
                          ? 'bg-gradient-to-b from-amber-500/15 to-amber-600/5 border-amber-400/40 shadow-lg shadow-amber-500/10'
                          : 'bg-white/5 border-white/10'
                      }`}
                      style={{ backfaceVisibility: 'hidden' }}
                    >
                      {plan.highlight && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="px-3 py-0.5 bg-amber-500 text-black text-[10px] font-bold rounded-full whitespace-nowrap">
                            {t('addon.popularBadge')}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className={`inline-block px-2.5 py-0.5 text-[10px] font-semibold rounded-full border ${plan.badgeColor} mb-2`}>
                          {plan.badge}
                        </span>
                        <h3 className="text-white font-bold text-xl">{plan.name}</h3>
                        <p className={`text-sm font-medium mt-0.5 ${plan.accentColor}`}>{plan.subtitle}</p>
                      </div>

                      <div className="p-4 bg-black/30 rounded-xl border border-white/10">
                        <div className="flex items-end gap-1">
                          <span className={`text-4xl font-bold ${plan.accentColor}`}>{plan.price}</span>
                          <span className="text-gray-400 text-sm mb-1">{t('addon.perMonth')}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-center gap-1.5 text-gray-400 text-xs">
                        <i className="ri-arrow-left-right-line text-amber-400/70"></i>
                        <span>{t('addon.flipViewFeatures')} {plan.features.length} {t('addon.flipFeaturesCount')}</span>
                      </div>
                    </div>

                    {/* Mặt sau */}
                    <div
                      className={`absolute inset-0 rounded-2xl border p-5 flex flex-col justify-between ${
                        plan.highlight
                          ? 'bg-gradient-to-b from-amber-500/15 to-amber-600/5 border-amber-400/40'
                          : 'bg-gray-900 border-white/10'
                      }`}
                      style={{
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-bold text-sm">{plan.name} — {t('addon.flipFeaturesCount')}</span>
                        <span className={`text-xs font-semibold ${plan.accentColor}`}>{plan.price} {t('addon.perMonth')}</span>
                      </div>

                      <ul className="space-y-1.5 flex-1 overflow-y-auto pr-1">
                        {plan.features.map((f, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-gray-300">
                            <i className={`ri-check-line mt-0.5 flex-shrink-0 ${plan.accentColor}`}></i>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFlip(plan.id); }}
                          className="flex-shrink-0 px-3 py-2 bg-white/10 border border-white/20 text-white text-xs font-semibold rounded-lg whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-arrow-go-back-line mr-1"></i>{t('addon.flipBack')}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onRegisterClick(); }}
                          className={`flex-1 py-2 rounded-lg font-semibold text-xs transition-all whitespace-nowrap cursor-pointer ${
                            plan.highlight
                              ? 'bg-amber-500 text-black hover:bg-amber-400'
                              : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                          }`}
                        >
                          {t('addon.flipCta')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Optional extras */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-400/30">
                <i className="ri-add-circle-line text-amber-400 text-lg"></i>
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-white font-bold text-base mb-1">{t('addon.extras.title')}</h4>
              <p className="text-gray-400 text-sm mb-3">{t('addon.extras.subtitle')}</p>
              <div className="flex flex-wrap gap-2">
                {extras.map((item, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-black/40 border border-white/15 rounded-full text-gray-300 text-xs font-medium"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={onRegisterClick}
                className="px-5 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                {t('addon.cta')}
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-500 text-xs mt-6">
          {t('addon.note')}
        </p>
      </div>
    </section>
  );
};

export default AddOnServicesSection;